import { createFileRoute, useNavigate, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import { supabase } from '@/lib/supabase'
import { primaryFactorId, verifyCode } from '@/lib/mfa'
import { useAuth } from '@/hooks/use-auth'
import { btnPrimary } from '@/components/rm'
import { BrandMark } from '@/components/brand'

// `next` brings a platform admin back to the console after the code (spec 009 US3.2). Only known
// paths are accepted, so the page cannot be used to send someone to another site.
export const Route = createFileRoute('/mfa-challenge')({
  validateSearch: (s: Record<string, unknown>): { next?: '/ops' } => (s.next === '/ops' ? { next: '/ops' } : {}),
  beforeLoad: async ({ search }) => {
    const { data } = await supabase.auth.getSession()
    if (!data.session) throw redirect({ to: '/login' })
    // Already satisfied (aal2) or no factor to satisfy → nothing to do here.
    const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
    if (!(aal?.currentLevel === 'aal1' && aal?.nextLevel === 'aal2')) {
      throw redirect({ to: search.next ?? '/dashboard' })
    }
  },
  component: MfaChallenge,
})

const inputCls =
  'h-12 w-full rounded-[10px] border border-token-subtle px-3 text-center text-[20px] tracking-[0.4em] text-token-primary outline-none transition-colors focus:border-token-strong focus:ring-2 focus:ring-brand/10'

function MfaChallenge() {
  const navigate = useNavigate()
  const { next } = Route.useSearch()
  const { refreshAal, signOut } = useAuth()
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [recovery, setRecovery] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(''); setBusy(true)
    try {
      if (recovery) {
        // Consume a backup code: the edge function resets the factor, so the
        // session drops to aal1 (no factor) and we can proceed, then re-enroll.
        const { data, error: fErr } = await supabase.functions.invoke('mfa-recovery', { body: { code: code.trim() } })
        if (fErr || !(data as { ok?: boolean })?.ok) throw new Error('bad code')
        await refreshAal()
        navigate({ to: '/account' })
        return
      }
      const factorId = await primaryFactorId()
      if (!factorId) throw new Error('No authenticator is set up on this account.')
      await verifyCode(factorId, code)
      await refreshAal()
      navigate({ to: next ?? '/dashboard' })
    } catch {
      setError(recovery ? 'That recovery code is invalid or already used.' : 'That code is wrong or expired. Check your authenticator app and try again.')
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas-subtle px-4">
      <div className="w-full max-w-sm">
        <div className="mb-7 text-center">
          <BrandMark className="mx-auto mb-3 h-11 w-11" />
          <h1 className="font-display text-[22px] font-bold tracking-[-0.02em] text-token-primary">Two-factor authentication</h1>
          <p className="mt-1 text-[13.5px] text-token-secondary">{recovery ? 'Enter one of your backup recovery codes.' : 'Enter the 6-digit code from your authenticator app.'}</p>
        </div>
        <form onSubmit={submit} className="space-y-4 rounded-[16px] border border-token-subtle bg-token-surface p-6">
          {recovery ? (
            <input
              autoFocus autoComplete="one-time-code" value={code}
              onChange={(e) => setCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 12))}
              className={`${inputCls} tracking-[0.15em]`} placeholder="xxxxx-xxxxx"
              aria-label="Backup recovery code"
            />
          ) : (
            <input
              autoFocus inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className={inputCls} placeholder="••••••"
              aria-label="6-digit authentication code"
            />
          )}
          {error && <p className="text-[13px] text-rose-600">{error}</p>}
          <button type="submit" disabled={busy || (recovery ? code.trim().length < 6 : code.length !== 6)} className={`${btnPrimary} h-10 w-full justify-center`}>{busy ? 'Verifying…' : recovery ? 'Use recovery code' : 'Verify'}</button>
          <button type="button" onClick={() => { setRecovery((r) => !r); setCode(''); setError('') }} className="w-full text-center text-[13px] font-semibold text-token-primary hover:underline">{recovery ? 'Use your authenticator app instead' : 'Use a backup recovery code'}</button>
          <button type="button" onClick={async () => { await signOut(); navigate({ to: '/login' }) }} className="w-full text-center text-[12.5px] font-semibold text-token-secondary hover:underline">Sign in with a different account</button>
        </form>
      </div>
    </div>
  )
}
